// QueryAsk — "ask the data" surface that sits at the top of the Funnel page.
//
// Three pieces:
//   1. Auto-scrolling row of hint chips (CSS marquee, pauses on hover, click
//      to run that catalog query).
//   2. Free-text input — picks the nearest catalog entry by token overlap.
//      Real NL→SQL is a future round; this gets the operator from
//      "I have a question" to "here's a chart" without leaving the dock.
//   3. Result card — chart, provenance, download-as-PNG button.

import { useEffect, useMemo, useRef, useState } from "react";

import { downloadSvgAsPng, sanitizeFilename } from "../lib/chart-export";
import {
  listCatalogQueries,
  nearestEntry,
  runCatalogQuery,
  type CatalogBundle,
  type CatalogEntry,
  type QueryRunResult,
} from "../lib/query-catalog";
import { Chart } from "./Chart";

interface RunState {
  entry: CatalogEntry;
  result: QueryRunResult | null;
  loading: boolean;
  error: string | null;
}

export function QueryAsk() {
  const [catalog, setCatalog] = useState<CatalogBundle | null>(null);
  const [input, setInput] = useState("");
  const [run, setRun] = useState<RunState | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setCatalog(await listCatalogQueries());
      } catch (e) {
        console.error("listCatalogQueries failed", e);
      }
    })();
  }, []);

  // We double the hint chip list inside the marquee track so the scroll
  // animation loops seamlessly (each half is identical width).
  const marqueeChips = useMemo(() => {
    const queries = catalog?.queries ?? [];
    return [...queries, ...queries];
  }, [catalog]);

  const ask = async (entry: CatalogEntry) => {
    setRun({ entry, result: null, loading: true, error: null });
    try {
      const result = await runCatalogQuery(entry.id);
      setRun({ entry, result, loading: false, error: null });
    } catch (e) {
      setRun({ entry, result: null, loading: false, error: String(e) });
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!catalog || catalog.queries.length === 0) return;
    const entry = nearestEntry(input.trim() || "funnel", catalog.queries);
    if (entry) void ask(entry);
  };

  const onDownload = async () => {
    if (!svgRef.current || !run?.entry) return;
    const filename = `${sanitizeFilename(run.entry.title)}.png`;
    await downloadSvgAsPng(svgRef.current, filename, {
      background: "#0a0d12",
      scale: 2,
    });
  };

  return (
    <div className="qa">
      <form onSubmit={onSubmit} className="qa-input-row">
        <span className="qa-bolt" aria-hidden="true">
          ✶
        </span>
        <input
          className="qa-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the data · e.g. 'which merchant issued the most coins?'"
        />
        <button type="submit" className="btn btn--primary" disabled={!catalog}>
          Ask
        </button>
      </form>

      {/* Scrolling hint chips */}
      {catalog && catalog.queries.length > 0 && (
        <div className="qa-marquee" aria-label="Suggested questions">
          <div className="qa-marquee__track">
            {marqueeChips.map((q, i) => (
              <button
                key={`${q.id}-${i}`}
                type="button"
                className="qa-chip"
                onClick={() => ask(q)}
                title={q.title}
              >
                <span className="qa-chip__bolt">✶</span>
                {q.hint}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Result */}
      {run && (
        <div className="qa-result">
          <div className="qa-result__head">
            <div>
              <div className="qa-result__eyebrow">
                ORACLE · {run.result?.status?.toUpperCase() ?? "RUNNING"}
                {run.result && (
                  <>
                    {" · "}
                    {run.result.elapsed_ms} ms · {run.result.source_label}
                  </>
                )}
              </div>
              <div className="qa-result__title">{run.entry.title}</div>
              {run.entry.subtitle && (
                <div className="qa-result__subtitle">{run.entry.subtitle}</div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                className="btn btn--ghost"
                type="button"
                onClick={() => ask(run.entry)}
                disabled={run.loading}
              >
                ↻ Re-run
              </button>
              <button
                className="btn btn--primary"
                type="button"
                onClick={onDownload}
                disabled={!run.result || run.loading}
              >
                ⬇ Download PNG
              </button>
              <button
                type="button"
                onClick={() => {
                  setRun(null);
                  setInput("");
                }}
                title="Clear result"
                style={{
                  background: "transparent",
                  border: "1px solid var(--edge)",
                  color: "var(--ink-faint)",
                  borderRadius: 4,
                  width: 32,
                  height: 32,
                  fontSize: 16,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  marginLeft: 4,
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {run.loading && (
            <div className="qa-loading">
              <span className="qa-loading__dot" />
              <span className="qa-loading__dot" />
              <span className="qa-loading__dot" />
              <span style={{ marginLeft: 12 }}>Running…</span>
            </div>
          )}

          {run.result && !run.loading && (
            <>
              <Chart ref={svgRef} spec={run.entry.chart} result={run.result} />
              {run.result.status === "sample" && (
                <div className="qa-provenance">
                  Sample data shown. To run live: open <strong>Settings → Data sources</strong>{" "}
                  and connect Metabase, then bind <code>source_id</code> +{" "}
                  <code>database_id</code> to the entry in{" "}
                  <code>resources/cohorts/query-catalog.json</code>.
                  {run.result.error && (
                    <div style={{ marginTop: 6, color: "var(--coral)" }}>
                      Last live attempt: {run.result.error}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {run.error && !run.loading && (
            <div className="qa-error">Couldn't run that query: {run.error}</div>
          )}
        </div>
      )}
    </div>
  );
}
